import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CircleDot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useSession } from '../components/session-context';
import ListFilterBar from '../components/work/ListFilterBar';
import { PageFrame } from '../components/work/page-chrome';
import { BrandEmpty, ErrorAlert, ListEmpty, LoadingRows, PageTitle } from '../components/work/page-states';
import { useDirectoryQuery } from '../components/work/use-directory-query';
import { MonoKey } from '../components/work/table-cells';
import { apiFetch, ApiError, orgSlug, v1Path } from '../lib/api';
import { compareText, matchesQuery, selectedAllows } from '../lib/directory-filter';
import { formatTimestampDateTime } from '../lib/dates';
import { devkitInstallCommand, mcpConfigSnippet, mcpServerUrl } from '../lib/mcp-config';
import { isMemberDto, parseItems, type MemberDto } from '../lib/work-types';

type TokenDto = {
  id: string;
  label: string;
  last_used_at: string | null;
  is_active: boolean;
  created_at: string;
  token?: string;
};

function isTokenDto(value: unknown): value is TokenDto {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { id?: unknown }).id === 'string' &&
    typeof (value as { label?: unknown }).label === 'string' &&
    typeof (value as { is_active?: unknown }).is_active === 'boolean' &&
    typeof (value as { created_at?: unknown }).created_at === 'string' &&
    ((value as { last_used_at?: unknown }).last_used_at === null ||
      typeof (value as { last_used_at?: unknown }).last_used_at === 'string')
  );
}

type OrgDto = {
  mcp_enabled: boolean;
};

function isOrgDto(value: unknown): value is OrgDto {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { mcp_enabled?: unknown }).mcp_enabled === 'boolean'
  );
}

const TOKEN_CHIPS = ['active'] as const;
const TOKEN_DEFAULT_ORDER = '-created_at';
const TOKEN_SORT_OPTIONS = [
  { value: '-created_at', label: 'Newest' },
  { value: 'created_at', label: 'Oldest' },
  { value: 'label', label: 'Label A-Z' },
] as const;

function formatStamp(value: string | null): string {
  if (!value) return '-';
  return formatTimestampDateTime(value) ?? '-';
}

export default function Tokens() {
  const { email } = useSession();
  const filters = useDirectoryQuery(TOKEN_CHIPS, TOKEN_DEFAULT_ORDER);
  const [items, setItems] = useState<TokenDto[]>([]);
  const [members, setMembers] = useState<MemberDto[]>([]);
  const [mcpEnabled, setMcpEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [copied, setCopied] = useState<'token' | 'config' | 'install' | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingMcp, setSavingMcp] = useState(false);
  const [savingTokenId, setSavingTokenId] = useState<string | null>(null);
  const togglingTokenId = useRef<string | null>(null);

  const visible = useMemo(() => {
    const rows = items.filter((token) => {
      const activeKey = token.is_active ? 'active' : 'inactive';
      if (!selectedAllows(filters.selected.active, activeKey)) return false;
      return matchesQuery(filters.query, [token.label]);
    });
    const orderBy = filters.orderBy ?? TOKEN_DEFAULT_ORDER;
    return rows.slice().sort((left, right) => {
      if (orderBy === 'created_at') return left.created_at.localeCompare(right.created_at);
      if (orderBy === 'label') return compareText(left.label || 'Untitled', right.label || 'Untitled');
      return right.created_at.localeCompare(left.created_at);
    });
  }, [filters.orderBy, filters.query, filters.selected.active, items]);

  const canManageMcp = useMemo(() => {
    if (!email) return false;
    const self = members.find((member) => member.email === email);
    return self?.role === 'admin' && self.is_active;
  }, [members, email]);

  const appOrigin = window.location.origin;
  const mcpUrl = mcpServerUrl(appOrigin);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tokens, org, memberRes] = await Promise.all([
        apiFetch<unknown>(v1Path('/tokens?limit=100&offset=0')),
        apiFetch<unknown>(`/api/v1/orgs/${orgSlug()}`),
        apiFetch<unknown>(v1Path('/members?limit=100&offset=0')),
      ]);
      if (!isOrgDto(org)) {
        throw new Error('Malformed organization response.');
      }
      setItems(parseItems(tokens, isTokenDto, 'API tokens'));
      setMcpEnabled(org.mcp_enabled);
      setMembers(parseItems(memberRes, isMemberDto, 'organization members'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tokens');
      setItems([]);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function copyText(value: string, which: 'token' | 'config' | 'install') {
    if (typeof navigator.clipboard?.writeText !== 'function') {
      setError('Clipboard is not available.');
      return;
    }
    try {
      await Promise.race([
        navigator.clipboard.writeText(value),
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Clipboard timed out.')), 2000);
        }),
      ]);
      setCopied(which);
    } catch {
      setError('Could not copy.');
    }
  }

  async function onToggleMcp(next: boolean) {
    if (!canManageMcp || savingMcp) return;
    setSavingMcp(true);
    setError(null);
    try {
      const org = await apiFetch<unknown>(`/api/v1/orgs/${orgSlug()}`, {
        method: 'PATCH',
        body: JSON.stringify({ mcp_enabled: next }),
      });
      if (!isOrgDto(org)) {
        throw new Error('Malformed organization response.');
      }
      setMcpEnabled(org.mcp_enabled);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Failed to update MCP');
    } finally {
      setSavingMcp(false);
    }
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, string> = {};
      if (label.trim()) body.label = label.trim();
      const created = await apiFetch<unknown>(v1Path('/tokens'), {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!isTokenDto(created) || typeof created.token !== 'string') {
        throw new Error('Malformed token response.');
      }
      setPlaintext(created.token);
      setCopied(null);
      setLabel('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  async function onToggleActive(id: string, next: boolean) {
    if (togglingTokenId.current) return;
    togglingTokenId.current = id;
    setSavingTokenId(id);
    setError(null);
    try {
      const updated = await apiFetch<unknown>(v1Path(`/tokens/${id}`), {
        method: 'PATCH',
        body: JSON.stringify({ is_active: next }),
      });
      if (!isTokenDto(updated)) {
        throw new Error('Malformed token response.');
      }
      setItems((current) => current.map((token) => (token.id === id ? { ...token, is_active: updated.is_active } : token)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Failed to update token');
    } finally {
      togglingTokenId.current = null;
      setSavingTokenId(null);
    }
  }

  async function onDelete(id: string) {
    setError(null);
    try {
      await apiFetch(v1Path(`/tokens/${id}`), { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-4">
      <PageTitle title="API tokens" />
      <p className="mb-4 text-sm text-muted-foreground">
        A token acts as you: the same org, streams, and permissions as the account that created it.
        Use <code>Authorization: Bearer bb_pat_...</code> for the API
        {mcpEnabled ? (
          <>
            {' '}and for the Pulse MCP server at <code>{mcpUrl}</code>
          </>
        ) : null}
        .
      </p>
      <div className="mb-6 border-t py-3">
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={mcpEnabled}
            disabled={!canManageMcp || savingMcp || loading}
            onCheckedChange={(value) => {
              if (value === 'indeterminate') return;
              void onToggleMcp(value);
            }}
          />
          <span>
            <span className="font-medium">Enable MCP</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              {canManageMcp
                ? 'Let AI agents use a personal access token to work in Pulse as that user.'
                : 'Ask an organization admin to enable MCP.'}
            </span>
          </span>
        </label>
      </div>
      {error ? <ErrorAlert>{error}</ErrorAlert> : null}
      {plaintext ? (
        <div className="mb-4 border-t py-3">
          <p className="text-sm font-medium">Copy this token now. It will not be shown again.</p>
          <div className="mt-1 flex items-start gap-2">
            <code className="min-w-0 flex-1 break-all font-mono text-xs">{plaintext}</code>
            <Button type="button" size="sm" variant="outline" onClick={() => void copyText(plaintext, 'token')}>
              {copied === 'token' ? 'Copied' : 'Copy'}
            </Button>
          </div>
          {mcpEnabled ? (
            <>
              <div className="mt-3 flex items-center justify-between gap-2">
                <p className="text-sm font-medium">Cursor MCP config</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void copyText(mcpConfigSnippet(plaintext, appOrigin), 'config')}
                >
                  {copied === 'config' ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <pre className="mt-1 overflow-x-auto rounded-md border bg-muted/40 p-3 font-mono text-xs">
                {mcpConfigSnippet(plaintext, appOrigin)}
              </pre>
              <div className="mt-3 flex items-center justify-between gap-2">
                <p className="text-sm font-medium">Cursor skills installer</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void copyText(devkitInstallCommand(plaintext, appOrigin), 'install')}
                >
                  {copied === 'install' ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Pulse will not serve this script without the token.
              </p>
              <pre className="mt-1 overflow-x-auto rounded-md border bg-muted/40 p-3 font-mono text-xs">
                {devkitInstallCommand(plaintext, appOrigin)}
              </pre>
            </>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">
              Enable MCP above to use this token with an AI agent.
            </p>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => {
              setPlaintext(null);
              setCopied(null);
            }}
          >
            Dismiss
          </Button>
        </div>
      ) : null}
      <form onSubmit={onCreate} className="mb-6 max-w-md space-y-2">
        <Label htmlFor="token-label">Label</Label>
        <div className="flex gap-2">
          <Input id="token-label" value={label} onChange={(e) => setLabel(e.target.value)} />
          <Button type="submit" size="sm" disabled={saving}>
            {saving ? 'Generating...' : 'Generate'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Optional name, for example Cursor.</p>
      </form>
      <ListFilterBar
        query={filters.query}
        onQueryChange={(query) => filters.apply({ query })}
        searchPlaceholder="Search label..."
        searchAriaLabel="Search tokens"
        chips={[
          {
            id: 'active',
            label: 'Status',
            icon: CircleDot,
            options: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ],
            selected: filters.selected.active,
            onToggle: (value) => filters.toggleChip('active', value),
          },
        ]}
        sort={{
          value: filters.orderBy ?? TOKEN_DEFAULT_ORDER,
          options: [...TOKEN_SORT_OPTIONS],
          onChange: (value) => filters.apply({ orderBy: value }),
        }}
        onClear={filters.clear}
      />
      {loading ? (
        <LoadingRows columns={4} />
      ) : items.length === 0 ? (
        <BrandEmpty title="No tokens yet." />
      ) : visible.length === 0 ? (
        <ListEmpty
          title="No tokens match"
          description="Clear filters if you expected matches."
          action={
            <Button type="button" variant="outline" size="sm" onClick={filters.clear}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-0">Label</TableHead>
              <TableHead className="hidden w-40 md:table-cell">Created</TableHead>
              <TableHead className="hidden w-40 lg:table-cell">Last used</TableHead>
              <TableHead className="w-24">Active</TableHead>
              <TableHead className="text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((token) => (
              <TableRow key={token.id}>
                <TableCell>
                  <p className="text-sm font-medium">{token.label || 'Untitled'}</p>
                  <p className="mt-0.5 font-mono text-xs tabular-nums text-muted-foreground md:hidden">
                    {formatStamp(token.created_at)}
                  </p>
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <MonoKey>{formatStamp(token.created_at)}</MonoKey>
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  <MonoKey>{formatStamp(token.last_used_at)}</MonoKey>
                </TableCell>
                <TableCell>
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={token.is_active}
                    disabled={savingTokenId === token.id}
                    aria-label={token.is_active ? `Deactivate ${token.label}` : `Activate ${token.label}`}
                    onClick={(event) => {
                      event.preventDefault();
                      void onToggleActive(token.id, !token.is_active);
                    }}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <Button type="button" size="sm" variant="outline" onClick={() => onDelete(token.id)}>
                    Revoke
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      </div>
    </PageFrame>
  );
}
